import { apiClient } from './client';
import type { PagedResult, Person, PersonCostCentres, PersonDetail, PersonWrite, ProvisionResult } from '@/types/oscar';

export interface PeopleQuery {
  status?: string;
  location?: number;
  search?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function toQueryString(q: PeopleQuery): string {
  const params = new URLSearchParams();
  if (q.status) params.set('status', q.status);
  if (q.location != null) params.set('location', String(q.location));
  if (q.search) params.set('search', q.search);
  if (q.sort) params.set('sort', q.sort);
  if (q.dir) params.set('dir', q.dir);
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** GET /api/people — filtered, paginated list. */
export function listPeople(query: PeopleQuery = {}, signal?: AbortSignal): Promise<PagedResult<Person>> {
  return apiClient.get<PagedResult<Person>>(`/api/people${toQueryString(query)}`, signal);
}

/** GET /api/people/:id — detail + related counts. */
export function getPerson(id: number, signal?: AbortSignal): Promise<PersonDetail> {
  return apiClient.get<PersonDetail>(`/api/people/${id}`, signal);
}

/** POST /api/people — create (helpdesk + admin). */
export function createPerson(body: PersonWrite): Promise<Person> {
  return apiClient.post<Person>('/api/people', body);
}

/** PATCH /api/people/:id — partial update (helpdesk + admin). */
export function updatePerson(id: number, body: PersonWrite): Promise<Person> {
  return apiClient.patch<Person>(`/api/people/${id}`, body);
}

/** POST /api/people/:id/delete — soft delete (admin only). */
export function deletePerson(id: number): Promise<{ PersonId: number; IsDeleted: boolean }> {
  return apiClient.post<{ PersonId: number; IsDeleted: boolean }>(`/api/people/${id}/delete`);
}

/** POST /api/people/:id/provision-software — force role-software provisioning (admin). */
export function provisionPersonSoftware(id: number): Promise<ProvisionResult> {
  return apiClient.post<ProvisionResult>(`/api/people/${id}/provision-software`);
}

/** GET /api/people/:id/cost-centres — the location default, the override split and what applies. */
export function getPersonCostCentres(id: number, signal?: AbortSignal): Promise<PersonCostCentres> {
  return apiClient.get<PersonCostCentres>(`/api/people/${id}/cost-centres`, signal);
}

/** PUT /api/people/:id/cost-centres — replace the split (totals 100); [] goes back to the location (helpdesk + admin). */
export function putPersonCostCentres(id: number, rows: { MinorCode: string; Percentage: number }[]): Promise<PersonCostCentres> {
  return apiClient.put<PersonCostCentres>(`/api/people/${id}/cost-centres`, { Rows: rows });
}
